import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { ExperimentationSplit, ExperimentationToggle } from '@interfaces';
import { Store } from '@ngrx/store';
import { GatekeeperService, WindowRef } from '@shared/services';
import { LogError } from '@store/index';
import { State, UpdateExperimentationToggle } from '@store/index';
import { CookieService } from 'ngx-cookie-service';
import { catchError, from, Observable, of, switchMap } from 'rxjs';

/**
 * Use this guard to ensure that experimentation toggle is loaded prior to activating,
 * and (optionally) route to new page if experimenting between two pages.
 *
 * 1. Set the `featureId` to the Gatekeeper featureId/toggle name that is controlling audience split
 * 2. (Optionally) Set the `experimentationRoute` to the route that should be routed to if gatekeeper returns true for featureId
 */
@Injectable({
  providedIn: 'root',
})
export class ExperimentationGuard implements CanActivate {
  constructor(
    private gateKeeper: GatekeeperService,
    private router: Router,
    private globalStore: Store<State>,
    private cookieService: CookieService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    if (this.cookieService.get('routeGuard') === 'OFF') {
      return of(true);
    }

    const experimentationRoute: string | undefined = route.data.experimentationRoute;
    const experimentationSplit: ExperimentationSplit | undefined = route.data.experimentationSplit;
    const featureId: ExperimentationToggle | undefined = route.data.featureId;
    if (!featureId || !experimentationSplit) {
      this.globalStore.dispatch(
        LogError({
          payload: {
            eventId: 'experimentation-guard--no-params',
            message: `featureId and experimentationSplit must be specified`,
            featureId,
            experimentationSplit,
            experimentationRoute,
          },
        }),
      );
      return of(true);
    }

    return this.gateKeeper.getExperimentationToggle(featureId, experimentationSplit).pipe(
      switchMap((value) => {
        this.globalStore.dispatch(UpdateExperimentationToggle({ toggle: featureId, value }));
        if (value && experimentationRoute) {
          this.router.navigate([experimentationRoute]);
        }
        return of(true);
      }),
      catchError((error) => {
        this.globalStore.dispatch(
          LogError({
            payload: {
              eventId: 'experimentation-guard--catch',
              message: `Caught error in experimentation guard`,
              featureId,
              experimentationRoute,
              error,
            },
          }),
        );
        return of(true);
      }),
    );
  }
}
